import { defineAsyncComponent } from 'vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

/**
 * The frontend route seam: every route the overlay invents, in one place.
 *
 * The settings registry reskins pages core already routes — but ticket states,
 * categories and sequences have no stock screen and therefore no core route to
 * key on. Something in core has to ask the overlay for routes, because Vue
 * Router has no `prepend_mod_with`: the table is assembled once, at module
 * scope, and nothing can inject into it uninvited. This module is what core
 * asks. The one-line spread in `dashboard/routes/index.js` is the seam's whole
 * core footprint, and adding a page here never touches core again.
 *
 * Three contracts every route in this list must honour, all asserted by spec:
 *
 * 1. **It is spread into `AppContainer`'s `children`** (`dashboard.routes.js`),
 *    never into the top-level table. Nesting is structural in vue-router, so a
 *    sibling matches *alone*: no shell, no sheet, no nav, no `.meridian` token
 *    scope — the whole chrome unmounts around the page. The first version of
 *    this seam got exactly that wrong, and every green check missed it because
 *    nothing resolved against the real table.
 * 2. **The path lives under `accounts/:accountId/…`, and the route appears in
 *    the navigation.** The shell works out which rail entry is lit by matching
 *    the active route against nav entries *first*, and only falls back to
 *    reading the URL segment. Until DEV-512 every invented page was a settings
 *    page, so "must be under `settings/`" was a serviceable stand-in for the
 *    rule that actually matters — be reachable from the nav, so the fallback
 *    is never needed. Orders and Products are operational screens rather than
 *    configuration, and burying them in the settings drawer would be wrong for
 *    the job they do, so the contract is now stated as what it always meant.
 * 3. **`meta.permissions` is present and non-empty.** Core's router guard is
 *    the only access control a frontend route gets; a route without it is an
 *    unguarded page. The frontend twin of "authorize before looking the
 *    record up". The nav reads the same meta to prune its rows, so guard and
 *    nav cannot drift.
 *
 * The route-level component never mounts on a custom install — the settings
 * sheet renders the registry's page and DashboardContent suppresses the
 * RouterView — and the route does not exist detached. It is here because a
 * route must render *something* if the suppression ever changes, and it must
 * be the same screen.
 */
const MeridianTicketStates = defineAsyncComponent(
  () =>
    import('meridian/screens/settings/ticketStates/MeridianTicketStates.vue')
);

// DEV-433: core has no notion of a category, so there is nothing to reskin.
const MeridianCategories = defineAsyncComponent(
  () => import('meridian/screens/settings/categories/MeridianCategories.vue')
);

// DEV-422: the manager's board. An operational screen rather than
// configuration, so it lives outside `settings/` and has no registry entry —
// the DEV-512 contract, which is why the route spec asserts nav-reachability
// rather than a URL prefix.
const MeridianTeamBoard = defineAsyncComponent(
  () => import('meridian/screens/teamBoard/MeridianTeamBoard.vue')
);

// DEV-447: the kinds of follow-up a business runs. Core has no notion of a
// task, so there is no stock screen to reskin and no core route to key on.
const MeridianTaskTypes = defineAsyncComponent(
  () => import('meridian/screens/settings/taskTypes/MeridianTaskTypes.vue')
);

// DEV-437: an ordered multi-message send has no core equivalent at all.
const MeridianSequences = defineAsyncComponent(
  () => import('meridian/screens/settings/sequences/MeridianSequences.vue')
);

// DEV-471: what the assistant looks for in conversations. Core has no such
// page — Captain's screens configure an assistant, not the signals it reads —
// so there is no core route to key the registry on.
const MeridianDataPoints = defineAsyncComponent(
  () => import('meridian/screens/settings/dataPoints/MeridianDataPoints.vue')
);

// DEV-466: Channels is a re-cut of the IA, not a reskin. Core routes the inbox
// *list* and the add-inbox wizard, but has no page that answers "what can a
// customer reach us on, and which accounts are connected to each" — that view
// spans inboxes rather than listing them, so there is no core route to key on.
const MeridianChannels = defineAsyncComponent(
  () => import('meridian/screens/settings/channels/MeridianChannels.vue')
);

// DEV-468: the widget's settings live inside one website inbox's show page in
// core. A page about the widget as a product surface — appearance, greeting,
// destination, embed snippet — has no route of its own.
const MeridianWebWidget = defineAsyncComponent(
  () => import('meridian/screens/settings/webWidget/MeridianWebWidget.vue')
);

// DEV-469: neither gate has a stock screen. Carrier state is read inside one
// inbox's settings, and consent does not exist at all.
const MeridianRegistrationConsent = defineAsyncComponent(
  () =>
    import(
      'meridian/screens/settings/registrationConsent/MeridianRegistrationConsent.vue'
    )
);

// DEV-474: the signed endpoints in and out, key rotation, and the delivery log.
// Core routes webhooks under the integrations settings and API keys under the
// user's profile; neither is this page, and neither is under Data.
const MeridianWebhooksApi = defineAsyncComponent(
  () => import('meridian/screens/settings/webhooksApi/MeridianWebhooksApi.vue')
);

// DEV-480: invented, because core has no page that is only notification and
// audio-alert settings — both live as sections on
// `settings/profile/Index.vue` today, which "Profile" (DEV-479, a reskin of
// core's own `profile_settings_index`) does not carry them onto.
const MeridianNotifications = defineAsyncComponent(
  () =>
    import('meridian/screens/settings/notifications/MeridianNotifications.vue')
);

// DEV-449: Branding has no stock screen at all — `disable_branding` is a
// Super-Admin-only toggle today, so there is nothing in core to reskin.
const MeridianBranding = defineAsyncComponent(
  () => import('meridian/screens/settings/branding/MeridianBranding.vue')
);

// DEV-449: Business hours is per-inbox in core, with no account-level screen
// to reskin — this is a directory into the existing per-inbox tab.
const MeridianBusinessHours = defineAsyncComponent(
  () =>
    import('meridian/screens/settings/businessHours/MeridianBusinessHours.vue')
);

// DEV-449: Company is a wholly new concept, backed by
// Custom::Api::V1::Accounts::CompanyController — no core route to reskin.
const MeridianCompany = defineAsyncComponent(
  () => import('meridian/screens/settings/company/MeridianCompany.vue')
);

// DEV-449: Compliance & Data has no core route either — core has no
// tenant-facing compliance screen at all.
const MeridianCompliance = defineAsyncComponent(
  () => import('meridian/screens/settings/compliance/MeridianCompliance.vue')
);

// DEV-511: invoice branding has no core equivalent — core has no notion of an
// invoice at all, so there is nothing to reskin.
const MeridianInvoice = defineAsyncComponent(
  () => import('meridian/screens/settings/integrations/MeridianInvoice.vue')
);

// DEV-512: the two commerce screens. Not settings pages — they are where a
// merchant works through the day's orders and looks up what is in stock — so
// they render through their own route component in the shell's content host
// rather than inside the settings sheet, and carry no registry entry.
const MeridianOrders = defineAsyncComponent(
  () => import('meridian/screens/commerce/MeridianOrders.vue')
);

const MeridianProducts = defineAsyncComponent(
  () => import('meridian/screens/commerce/MeridianProducts.vue')
);

/**
 * DEV-538: the setup wizard.
 *
 * Core declares both wizard routes **top-level** in `dashboard.routes.js`, not
 * as `AppContainer` children, so the settings registry cannot reach them. This
 * seam can: a child of `AppContainer` resolving the same URL shadows the
 * top-level record, because equal path scores resolve to whichever registered
 * first and children register with their parent — above the onboarding records.
 *
 * Two conditions, each of which fails catastrophically rather than visibly:
 *
 * 1. **The names must contain `onboarding_`.** `isOnOnboardingView` in
 *    `v3/helpers/RouteHelper.js` is `name.includes('onboarding_')`, and the
 *    router guard sends any route it does not recognise as an onboarding view
 *    back to the wizard — so a wrongly named step is redirected away from on
 *    arrival, arrives again, and loops forever.
 * 2. **The paths must be core's exactly.** The guard redirects by *path*,
 *    derived from the backend cursor (`onboarding` / `onboarding/inbox-setup`),
 *    never by name. A route on a different URL is simply never reached.
 */
const MeridianOnboardingAccountDetails = defineAsyncComponent(
  () =>
    import('meridian/screens/onboarding/MeridianOnboardingAccountDetails.vue')
);

const MeridianOnboardingInboxSetup = defineAsyncComponent(
  () => import('meridian/screens/onboarding/MeridianOnboardingInboxSetup.vue')
);

/**
 * DEV-540: the WhatsApp step. A *sub-step*, and the difference matters.
 *
 * The two records above shadow core's own; this one shadows nothing — core has
 * no third wizard URL, because its step vocabulary is fixed at
 * `account_details → inbox_setup → done` in a core file. So the cursor stays
 * `inbox_setup` while the client is here, and only the URL moves, which is what
 * makes a refresh resume on this step instead of the one before it.
 *
 * Condition 1 still applies in full and is the reason for the name: the guard
 * recognises an onboarding view by `name.includes('onboarding_')`, and this
 * route is reached while the cursor still says `inbox_setup`. Condition 2 does
 * not — there is no core path to match, only the requirement that the path sit
 * under core's `onboarding/` prefix so it reads as one flow.
 */
const MeridianOnboardingWhatsApp = defineAsyncComponent(
  () => import('meridian/screens/onboarding/MeridianOnboardingWhatsApp.vue')
);

// DEV-550: the Shopify step. The same sub-step shape as WhatsApp above — its
// own URL under core's `onboarding/` prefix, `onboarding_` in the name, and
// the cursor left on `inbox_setup`. It is also the URL Shopify's OAuth round
// trip returns to (`RETURN_PATHS['onboarding']`), which is why it must be a
// real, refreshable address rather than a step held in memory.
const MeridianOnboardingShopify = defineAsyncComponent(
  () => import('meridian/screens/onboarding/MeridianOnboardingShopify.vue')
);

const MeridianSecurityAccess = defineAsyncComponent(
  () => import('meridian/screens/settings/security/MeridianSecurityAccess.vue')
);

/**
 * The Chatwoot feature flag the two Commerce screens sit behind.
 *
 * A raw slug rather than a `FEATURE_FLAGS` member because that is how the nav
 * entry names it too, and the two must agree: the nav entry is what prunes the
 * rail row, and this is what says the route belongs to the same feature.
 */
const COMMERCE_FLAG = 'shopify_integration';

/**
 * Called, not read: the gate resolves `isCustom` at call time because a
 * module-scope read burned DEV-429 — the auth gate silently never fired and a
 * custom install rendered stock Chatwoot with no error anywhere. Detached this
 * returns nothing and the router is byte-identical to upstream's.
 */
export const customRoutes = () => {
  // `=== 'true'`, never truthiness: the layout interpolates a Ruby boolean
  // into a quoted JS string, so the runtime value is 'true' or 'false' — and
  // 'false' is truthy. Every reader of this flag in the tree compares the
  // string (useConfig, themeHelper, the v3 auth gate); so does this one.
  if (window.chatwootConfig?.isCustom !== 'true') return [];

  return [
    {
      path: frontendURL('accounts/:accountId/onboarding'),
      name: 'meridian_onboarding_account_details',
      component: MeridianOnboardingAccountDetails,
      meta: {
        // Core's own list for this route, unchanged. The guard already
        // restricts the wizard to administrators of an account that still has
        // a cursor; this is the route-level floor, not the decision.
        permissions: ['administrator', 'agent', 'custom_role'],
        // Rendered without the shell's chrome. The wizard is a pre-dashboard
        // screen — a rail and a top bar pointing at an app the client has not
        // finished creating would be navigation to nowhere.
        meridianFullScreen: true,
      },
    },
    {
      path: frontendURL('accounts/:accountId/onboarding/inbox-setup'),
      name: 'meridian_onboarding_inbox_setup',
      component: MeridianOnboardingInboxSetup,
      meta: {
        permissions: ['administrator', 'agent', 'custom_role'],
        meridianFullScreen: true,
      },
    },
    {
      // No `featureFlag`, for the same reason the commerce screens carry one
      // and this does not: those are gated per account, while a wizard step is
      // decided by whether the installation has a Partner app at all. The
      // account-level answer still applies — the availability check reads an
      // endpoint that refuses an unentitled account — but it is not this
      // route's to declare.
      path: frontendURL('accounts/:accountId/onboarding/shopify'),
      name: 'meridian_onboarding_shopify',
      component: MeridianOnboardingShopify,
      meta: {
        permissions: ['administrator', 'agent', 'custom_role'],
        meridianFullScreen: true,
      },
    },
    {
      // No `featureFlag`: WhatsApp availability is an *installation* fact —
      // whether the Meta app exists — not an account entitlement, and it is
      // decided by `useChannelConfig` where the step list is built. A flag here
      // would read as a second gate that nothing consults.
      path: frontendURL('accounts/:accountId/onboarding/whatsapp'),
      name: 'meridian_onboarding_whatsapp',
      component: MeridianOnboardingWhatsApp,
      meta: {
        permissions: ['administrator', 'agent', 'custom_role'],
        meridianFullScreen: true,
      },
    },
    {
      path: frontendURL('accounts/:accountId/settings/ticket-states'),
      name: 'settings_ticket_states',
      component: MeridianTicketStates,
      meta: {
        permissions: ['administrator'],
      },
    },
    {
      path: frontendURL('accounts/:accountId/commerce/orders'),
      name: 'commerce_orders',
      component: MeridianOrders,
      meta: {
        // Administrator-only, because of what the screen shows rather than
        // what it does: every row carries a customer's name, phone and full
        // address, for the whole account. An agent reaches their own
        // customers' orders through the conversation panel, which is scoped to
        // the tickets they can open. The server enforces the same.
        permissions: ['administrator'],
        // A real Chatwoot flag, unlike the invented pages' own slugs, declared
        // the way core declares it on its own flagged routes. It gates nothing
        // by itself — nothing in the router reads it — but it keeps the route
        // and the nav entry naming the same flag, and the nav entry is what
        // prunes the rail row. The backend is the gate that matters.
        featureFlag: COMMERCE_FLAG,
      },
    },
    {
      path: frontendURL('accounts/:accountId/commerce/products'),
      name: 'commerce_products',
      component: MeridianProducts,
      meta: {
        permissions: ['administrator', 'agent'],
        featureFlag: COMMERCE_FLAG,
      },
    },
    {
      path: frontendURL('accounts/:accountId/settings/invoices'),
      name: 'settings_invoices',
      component: MeridianInvoice,
      meta: {
        // Administrator only: this decides what every customer of the account
        // receives, and the preview endpoint behind it renders arbitrary
        // posted configuration. The server enforces the same on both.
        permissions: ['administrator'],
      },
    },
    {
      path: frontendURL('accounts/:accountId/team-board'),
      name: 'team_board',
      component: MeridianTeamBoard,
      meta: {
        // Administrators and anyone holding `oversee_team_work`. The router
        // guard cannot express a custom capability, so it names the base role
        // it can and the API refuses everyone else — which is where the real
        // check belongs anyway (R23.14).
        permissions: ['administrator', 'agent'],
      },
    },
    {
      path: frontendURL('accounts/:accountId/settings/task-types'),
      name: 'settings_task_types',
      component: MeridianTaskTypes,
      meta: {
        // Administrator only. A type is configuration: an agent raising a
        // refund picks from the list but never edits it, and the controller
        // refuses a non-admin regardless of what the router allows.
        permissions: ['administrator'],
      },
    },
    {
      path: frontendURL('accounts/:accountId/settings/categories'),
      name: 'settings_categories',
      component: MeridianCategories,
      meta: {
        // Administrator only, like ticket states: a category is vocabulary the
        // account defines, and the whole page is the CRUD for it. The
        // agent-facing picker is a separate surface with its own access to
        // decide, and the controller is admin-only server-side regardless.
        permissions: ['administrator'],
      },
    },
    {
      path: frontendURL('accounts/:accountId/settings/data-points'),
      name: 'settings_data_points',
      component: MeridianDataPoints,
      meta: {
        // Administrator only. A data point decides what the assistant reads
        // every conversation for, which is an account-wide commitment of spend
        // and accuracy rather than an agent's own working material — and the
        // controller refuses a non-administrator regardless, because a hidden
        // nav entry is not access control.
        permissions: ['administrator'],
      },
    },
    {
      path: frontendURL('accounts/:accountId/settings/sequences'),
      name: 'settings_sequences',
      component: MeridianSequences,
      meta: {
        // Agents included, unlike ticket states and categories. A sequence is
        // an agent's own working material — the catalogue they send twenty
        // times a day — and `visibility` decides who sees whose. The server
        // scopes reads per user and refuses an agent's write to shared
        // material, so the route does not need to.
        permissions: ['administrator', 'agent'],
      },
    },
    {
      // Open to every role, matching the prototype: Branding carries no
      // `adminOnly` flag, unlike Company/Security/Compliance. Writes are
      // gated inside the page itself (an "Admins only" card, not a route
      // block) and enforced server-side by BrandingSettingsController.
      path: frontendURL('accounts/:accountId/settings/branding'),
      name: 'settings_branding',
      component: MeridianBranding,
      meta: {
        permissions: ['administrator', 'agent'],
      },
    },
    {
      // Restricted to administrator even though the prototype doesn't mark
      // `s_hours` adminOnly: every row here links into `settings_inbox_show`,
      // which core itself gates to administrators. Showing this list to an
      // agent would be a directory of links that all 403 on click.
      path: frontendURL('accounts/:accountId/settings/business-hours'),
      name: 'settings_business_hours',
      component: MeridianBusinessHours,
      meta: {
        permissions: ['administrator'],
      },
    },
    {
      // Open to every role at the ROUTE level, unlike Business hours: the
      // page itself renders the prototype's own "Admins only" card for a
      // non-admin rather than a dead end, so gating the route would only
      // duplicate what the component already handles gracefully. The
      // controller is still administrator-only server-side.
      path: frontendURL('accounts/:accountId/settings/company'),
      name: 'settings_company',
      component: MeridianCompany,
      meta: {
        permissions: ['administrator', 'agent'],
      },
    },
    {
      // Its own route rather than a reskin of core's `security_settings_index`.
      //
      // That route carries `featureFlag: SAML`, and the Free plan has no
      // features — so reskinning it put the whole page behind SAML and hid the
      // security health check, sign-in methods and 2FA from the tier most
      // likely to want a security readout. Only the SAML card needs the flag,
      // and it checks for itself now.
      //
      // Open to every role at the route level, like Company and Compliance:
      // the page draws its own "Admins only" card for a non-admin.
      path: frontendURL('accounts/:accountId/settings/security-access'),
      name: 'settings_security_access',
      component: MeridianSecurityAccess,
      meta: {
        permissions: ['administrator', 'agent'],
      },
    },
    {
      // Same shape as Company: open at the route level, gated by the
      // component's own "Admins only" card. The one real read
      // (SubscriptionAPI) is account-scoped, not admin-only server-side.
      path: frontendURL('accounts/:accountId/settings/compliance'),
      name: 'settings_compliance',
      component: MeridianCompliance,
      meta: {
        permissions: ['administrator', 'agent'],
      },
    },
    {
      // Administrator only, matching every connect flow it leads into: core
      // gates `settings_inbox_new` and `settings_inbox_show` to administrators,
      // so an agent seeing this board would get a wall of buttons that 403.
      path: frontendURL('accounts/:accountId/settings/channels'),
      name: 'settings_channels',
      component: MeridianChannels,
      meta: {
        permissions: ['administrator'],
      },
    },
    {
      // Administrator only for the same reason: every write it makes is a
      // website-inbox update, which core restricts to administrators.
      path: frontendURL('accounts/:accountId/settings/web-widget'),
      name: 'settings_web_widget',
      component: MeridianWebWidget,
      meta: {
        permissions: ['administrator'],
      },
    },
    {
      // Administrator only. Both halves are administrator concerns — the
      // carrier's view of the business name, and who the account is allowed to
      // market to — and the consent endpoints refuse an agent server-side.
      path: frontendURL('accounts/:accountId/settings/registration-consent'),
      name: 'settings_registration_consent',
      component: MeridianRegistrationConsent,
      meta: {
        permissions: ['administrator'],
      },
    },
    {
      // DEV-474. Administrator only, and the API refuses an agent server-side
      // as well — a delivery row carries the url an account posts to and
      // whatever a failing endpoint said back, and an API key is not something
      // a route guard should be the only thing standing in front of.
      path: frontendURL('accounts/:accountId/settings/webhooks-api'),
      name: 'settings_webhooks_api',
      component: MeridianWebhooksApi,
      meta: {
        permissions: ['administrator'],
      },
    },
    {
      // DEV-480. Per-user, not an admin concern — every role reaches their
      // own notification and audio-alert settings, matching core's own
      // `Policy`-gated sections rather than a route-level admin restriction.
      path: frontendURL('accounts/:accountId/settings/notifications'),
      name: 'settings_notifications',
      component: MeridianNotifications,
      meta: {
        permissions: ['administrator', 'agent'],
      },
    },
  ];
};
